import type { TFunction } from 'i18next';
import type { BulkItemError } from '@/api/allocations';
import { backendNumber } from '@/api/errorDetails';
import { ApiError, ErrorCodes, isApiError } from '@/api/errors';
import { formatMoneyMinor } from '@/i18n/format';

/** Catalog key for a named allocation / account write error; `null` for `SUBSCRIPTION_REQUIRED`. */
export function allocationErrorKey(error: unknown): string | null {
  if (!isApiError(error)) return 'accounts.errorGeneric';
  switch (error.code) {
    case ErrorCodes.SUBSCRIPTION_REQUIRED:
      return null;
    case ErrorCodes.ALLOCATION_EXCEEDS_REMAINDER:
      return 'accounts.errorExceedsRemainder';
    case ErrorCodes.ALLOCATION_SIGN_MISMATCH:
      return 'accounts.errorSign';
    case ErrorCodes.ALLOCATION_CURRENCY_MISMATCH:
      return 'allocations.errorCurrencyMismatch';
    case ErrorCodes.CARD_PAYMENT_NOT_ALLOCATABLE:
      return 'accounts.errorCardPayment';
    case ErrorCodes.FORBIDDEN:
    case ErrorCodes.PERMISSION_REQUIRED:
      return 'accounts.errorPermission';
    case ErrorCodes.ALLOCATION_ALREADY_REVERSED:
      return 'accounts.errorAlreadyReversed';
    case ErrorCodes.TRANSACTION_HAS_ALLOCATIONS:
      return 'accounts.errorVoidHasAllocations';
    case ErrorCodes.ACCOUNT_CLOSED:
      return 'accounts.errorClosed';
    case ErrorCodes.GRANTEE_NOT_SUBSCRIBER:
      return 'accounts.errorGranteeNotSubscriber';
    case ErrorCodes.ACCOUNT_CURRENCY_IMMUTABLE:
      return 'accounts.errorCurrencyImmutable';
    case ErrorCodes.VALIDATION_FAILED:
      return 'accounts.errorValidation';
    case ErrorCodes.IDEMPOTENCY_CONFLICT:
      return 'accounts.errorIdempotency';
    case ErrorCodes.USER_FROZEN:
      return 'errors.userFrozen';
    default:
      return 'accounts.errorGeneric';
  }
}

export function bulkItemErrorKey(code: string): string {
  switch (code) {
    case ErrorCodes.ALLOCATION_EXCEEDS_REMAINDER:
      return 'accounts.errorExceedsRemainder';
    case ErrorCodes.ALLOCATION_SIGN_MISMATCH:
      return 'accounts.errorSign';
    case ErrorCodes.ALLOCATION_CURRENCY_MISMATCH:
      return 'allocations.errorCurrencyMismatch';
    case ErrorCodes.CARD_PAYMENT_NOT_ALLOCATABLE:
      return 'accounts.errorCardPayment';
    case ErrorCodes.FORBIDDEN:
    case ErrorCodes.PERMISSION_REQUIRED:
      return 'accounts.errorPermission';
    case ErrorCodes.ACCOUNT_CLOSED:
      return 'accounts.errorClosed';
    case ErrorCodes.IDEMPOTENCY_CONFLICT:
      return 'accounts.errorIdempotency';
    case ErrorCodes.USER_FROZEN:
      return 'errors.userFrozen';
    default:
      return 'accounts.errorGeneric';
  }
}

/**
 * Translated allocation-write message. `ALLOCATION_EXCEEDS_REMAINDER` interpolates
 * `remainderMinor` when the backend sent it. A part the caller can see but has no authority on
 * (TMMAPI-0146: `403 PERMISSION_REQUIRED {permission: can_edit}`) names the line's account when the
 * caller knows it; a `404` stays the not-found path (MM-D-05).
 */
export function allocationErrorMessage(
  error: unknown,
  t: TFunction,
  operatingCurrency: string | null,
  locale: string,
  accountName?: string,
): string | null {
  if (
    accountName !== undefined &&
    isApiError(error) &&
    error.code === ErrorCodes.PERMISSION_REQUIRED &&
    error.details.backend?.permission === 'can_edit'
  ) {
    return t('roles.reverseNeedsEdit', { account: accountName });
  }
  if (isApiError(error) && error.code === ErrorCodes.ALLOCATION_EXCEEDS_REMAINDER) {
    const remainder = backendNumber(error, 'remainderMinor');
    if (remainder !== null) {
      return t('allocations.exceedsRemainder', {
        amount: formatMoneyMinor(remainder, operatingCurrency, locale),
      });
    }
  }
  const key = allocationErrorKey(error);
  return key === null ? null : t(key);
}

/** Per-item bulk `2xx` ERROR — wraps `item.details` so `remainderMinor` is readable. */
export function bulkItemErrorMessage(
  item: BulkItemError,
  t: TFunction,
  operatingCurrency: string | null,
  locale: string,
): string {
  return (
    allocationErrorMessage(
      new ApiError(item.code, 0, undefined, {
        message: item.message,
        backend: item.details ?? undefined,
      }),
      t,
      operatingCurrency,
      locale,
    ) ?? t('accounts.errorGeneric')
  );
}

/**
 * The keys of the rows bound for the virtual account tmm-api refused with `409
 * ALLOCATION_CURRENCY_MISMATCH {virtualAccountId}` (TMMAPI-0205: a plan slice or a rule target into
 * a shared virtual account kept in another currency); `[]` for any other error, no `details`, or no
 * row bound there (the caller's dialog-level message is then the fallback). Ids only: tmm-api
 * compares the currencies, never the SPA.
 */
export function currencyRefusedRows(
  error: unknown,
  rows: readonly { key: string; virtualAccountId: number | undefined }[],
): string[] {
  if (!isApiError(error) || error.code !== ErrorCodes.ALLOCATION_CURRENCY_MISMATCH) return [];
  const refused = backendNumber(error, 'virtualAccountId');
  if (refused === null) return [];
  return rows.filter((row) => row.virtualAccountId === refused).map((row) => row.key);
}
